import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Building2, ChevronRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * PMT1 — "Which business do you want to work in?"
 *
 * Shown ONLY when the verified person holds more than one active membership. It is a plain product
 * surface, not an identity-management console: it renders the business name (and the brand
 * identifier, which is the identifier staff already use to join a business) and nothing else. No
 * role metadata, no provider/resource classification, no industry-profile or commercial data, no
 * internal tenant ids, no other tenant's staff.
 *
 * Choosing here only REQUESTS a context — the caller must have the server verify the membership
 * before any tenant is entered.
 */
const TenantSelector = ({ memberships = [], onSelect, onCancel, cancelLabel, error, busy }) => {
  const { t } = useLanguage();
  const [pending, setPending] = useState(null);

  const choose = async (brandId) => {
    setPending(brandId);
    try {
      await onSelect(brandId);
    } finally {
      setPending(null);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-purple-600" />
          {t('tenantSelect.title')}
        </CardTitle>
        <CardDescription>{t('tenantSelect.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <p role="alert" className="text-sm text-red-600">{error}</p>
        )}

        {memberships.length === 0 ? (
          <p className="text-sm text-gray-500">{t('tenantSelect.none')}</p>
        ) : (
          memberships.map((m) => (
            <button
              key={m.brand_id}
              type="button"
              disabled={busy || pending !== null}
              onClick={() => choose(m.brand_id)}
              aria-label={m.business_name || m.brand_id}
              className="w-full flex items-center justify-between rounded-lg border border-gray-200 p-4 text-left transition hover:border-purple-300 hover:bg-purple-50 disabled:opacity-60"
            >
              <span className="min-w-0">
                <span className="block font-medium text-gray-900 truncate">
                  {m.business_name || m.brand_id}
                </span>
                <span className="block text-xs text-gray-500 truncate">{m.brand_id}</span>
              </span>
              {pending === m.brand_id
                ? <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                : <ChevronRight className="h-4 w-4 text-gray-400" />}
            </button>
          ))
        )}

        {onCancel && (
          <Button variant="outline" className="w-full" onClick={onCancel} disabled={busy}>
            {cancelLabel || t('common.cancel')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default TenantSelector;
